import React, { useState, useEffect, useRef } from 'react';
import './App.css';

/* ═══════════════════════════════════════════════
   NAVBAR
═══════════════════════════════════════════════ */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const links = [
    { label: 'About',    id: 'about' },
    { label: 'Services', id: 'services' },
    { label: 'How It Works', id: 'how' },
    { label: 'Pricing',  id: 'pricing' },
    { label: 'Why Us',   id: 'why' },
  ];

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : 'navbar-default'}`}>
      <div className="navbar-inner">
        <div className="nav-logo">STRATEG<span>IX</span></div>
        <div className="nav-links">
          {links.map(l => (
            <button key={l.id} className="nav-link" style={{ background:'none', border:'none', cursor:'pointer' }}
              onClick={() => scrollTo(l.id)}>{l.label}</button>
          ))}
        </div>
        <button className="nav-cta" onClick={() => scrollTo('contact')}>
          Get a Quote
        </button>
        <button className="mobile-toggle" onClick={() => setMenuOpen(!menuOpen)}>
          <i className={`fas fa-${menuOpen ? 'times' : 'bars'}`}></i>
        </button>
      </div>
      <div className={`mobile-nav ${menuOpen ? 'open' : 'closed'}`}>
        <div className="mobile-nav-inner">
          {links.map(l => (
            <button key={l.id} className="mobile-nav-link"
              style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left' }}
              onClick={() => scrollTo(l.id)}>{l.label}</button>
          ))}
          <button className="btn-primary" style={{ alignSelf:'flex-start' }}
            onClick={() => scrollTo('contact')}>Get a Quote</button>
        </div>
      </div>
    </nav>
  );
}

/* ═══════════════════════════════════════════════
   HERO
═══════════════════════════════════════════════ */
function Hero() {
  return (
    <section className="hero" id="home">
      <div className="grid-overlay" />
      <div className="hero-bg-glow" />
      <div className="hero-bg-glow2" />
      <div className="hero-content">
        <div className="hero-text animate-in">
          <div className="hero-mono">
            <span>Remote. Dedicated. Scalable.</span>
          </div>
          <h1>
            Build your<br />
            <span className="accent">elite dev team</span>
            <span className="line2"> on demand</span>
          </h1>
          <p className="hero-desc">
            We provide dedicated software development teams at competitive rates —
            without sacrificing quality, speed, or transparency. From 1 developer
            to a full squad of 10, assembled within 72 hours.
          </p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
              <i className="fas fa-rocket"></i> Build my team
            </button>
            <button className="btn-outline" onClick={() => document.getElementById('how').scrollIntoView({ behavior: 'smooth' })}>
              <i className="fas fa-play"></i> How it works
            </button>
          </div>
          <div className="hero-metrics">
            <div className="hero-metric">
              <span className="num">200<span>+</span></span>
              <span className="lbl">Projects delivered</span>
            </div>
            <div className="hero-metric">
              <span className="num">72<span>h</span></span>
              <span className="lbl">Average onboarding</span>
            </div>
            <div className="hero-metric">
              <span className="num">98<span>%</span></span>
              <span className="lbl">Client satisfaction</span>
            </div>
          </div>
        </div>

        <div className="hero-visual animate-in delay-2">
          <div className="terminal-card">
            <div className="terminal-bar">
              <div className="terminal-dot red" />
              <div className="terminal-dot yellow" />
              <div className="terminal-dot green" />
              <span className="terminal-title">team-config.json — Strategix</span>
            </div>
            <div className="terminal-body">
              <div className="t-line"><span className="t-prompt">$</span><span className="t-cmd"> create-team --project=my-app</span></div>
              <div className="t-line"><span className="t-output">✓ Initializing dedicated team...</span></div>
              <div className="t-line" style={{marginTop:'10px'}}><span className="t-comment">// Team configuration</span></div>
              <div className="t-line"><span className="t-key">"size"</span><span className="t-cmd">: </span><span className="t-val">"5 developers"</span><span className="t-cmd">,</span></div>
              <div className="t-line"><span className="t-key">"stack"</span><span className="t-cmd">: </span><span className="t-val">["React", "Node.js", "AWS"]</span><span className="t-cmd">,</span></div>
              <div className="t-line"><span className="t-key">"hours"</span><span className="t-cmd">: </span><span className="t-val">"full-time"</span><span className="t-cmd">,</span></div>
              <div className="t-line"><span className="t-key">"timezone"</span><span className="t-cmd">: </span><span className="t-val">"CET +1"</span><span className="t-cmd">,</span></div>
              <div className="t-line"><span className="t-key">"level"</span><span className="t-cmd">: </span><span className="t-val">"senior"</span><span className="t-cmd">,</span></div>
              <div className="t-line"><span className="t-key">"start"</span><span className="t-cmd">: </span><span className="t-val">"in 72 hours"</span></div>
              <div className="t-line" style={{marginTop:'10px'}}><span className="t-output">✓ 5 engineers matched</span></div>
              <div className="t-line"><span className="t-output">✓ Contracts ready</span></div>
              <div className="t-line"><span className="t-prompt">$</span><span className="t-cmd"> </span><span className="t-cursor" /></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   CLIENTS STRIP
═══════════════════════════════════════════════ */
function ClientsStrip() {
  const clients = ['Fintech Corp', 'NovaSaaS', 'RetailEdge', 'HealthTech AG', 'Logistix', 'MediaVault', 'CloudBase'];
  return (
    <div className="clients-strip">
      <div className="clients-inner">
        <span className="clients-label">Trusted by</span>
        <div className="clients-logos">
          {clients.map(c => <span key={c} className="client-chip">{c}</span>)}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ABOUT
═══════════════════════════════════════════════ */
function About() {
  return (
    <section className="section" id="about">
      <div className="container">
        <div className="about-grid">
          <div className="about-visual">
            <div className="about-map">
              <div className="map-card">
                <span className="map-num">15+</span>
                <span className="map-lbl">Countries served</span>
              </div>
              <div className="map-card">
                <span className="map-num">200+</span>
                <span className="map-lbl">Developers network</span>
              </div>
              <div className="map-card">
                <span className="map-num">7yr</span>
                <span className="map-lbl">In operation</span>
              </div>
              <div className="map-card featured">
                <div className="map-featured-text">
                  <h4>ISO-certified delivery process</h4>
                  <p>Structured, transparent, and fully documented workflows for every engagement.</p>
                </div>
                <i className="fas fa-shield-check map-featured-icon"></i>
              </div>
              <div className="map-card">
                <span className="map-num">72h</span>
                <span className="map-lbl">Onboarding</span>
              </div>
              <div className="map-card">
                <span className="map-num">98%</span>
                <span className="map-lbl">Satisfaction</span>
              </div>
              <div className="map-card">
                <span className="map-num">40%</span>
                <span className="map-lbl">Avg. cost saving</span>
              </div>
            </div>
          </div>

          <div className="about-text">
            <span className="badge">About Strategix</span>
            <h2 className="section-heading">We are not an agency.<br />We are your <em>remote tech arm</em>.</h2>
            <p className="section-sub" style={{ marginBottom: '28px' }}>
              Strategix was founded on one conviction: that world-class software development
              should be accessible to any business, regardless of budget or geography.
              We connect companies with pre-vetted, senior-calibre developers who integrate
              directly into your workflows, culture, and tools.
            </p>
            <p className="section-sub" style={{ marginBottom: '32px' }}>
              Unlike traditional outsourcing firms, we don't throw bodies at problems.
              We build precise, right-sized teams aligned to your specific tech stack,
              timeline, and business objectives — then stay accountable for outcomes.
            </p>
            <div className="about-pillars">
              {[
                { icon: 'fa-bolt', title: 'Speed without shortcuts', desc: 'Teams assembled in 72 hours. Production-ready from day one.' },
                { icon: 'fa-lock', title: 'Full IP ownership', desc: 'You own every line of code, every asset, every repository.' },
                { icon: 'fa-comments', title: 'Transparent communication', desc: 'Daily standups, weekly reports, real-time Slack/Teams access.' },
                { icon: 'fa-expand-arrows-alt', title: 'Scale up or down instantly', desc: 'Add or remove developers as your project evolves.' },
              ].map(p => (
                <div key={p.title} className="pillar">
                  <div className="pillar-icon"><i className={`fas ${p.icon}`}></i></div>
                  <div className="pillar-text">
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   SERVICES
═══════════════════════════════════════════════ */
function Services() {
  const services = [
    {
      num: '01', icon: 'fa-code', featured: false,
      title: 'Full Project Development',
      desc: 'End-to-end delivery of your software product — from discovery and architecture to launch and post-release support. We own the full SDLC.',
      tags: ['Discovery', 'Architecture', 'QA', 'DevOps', 'Launch'],
    },
    {
      num: '02', icon: 'fa-puzzle-piece', featured: false,
      title: 'Partial Outsourcing',
      desc: 'Plug our engineers into specific modules, features, or technical debt areas. Ideal when you need targeted expertise without disrupting your existing team.',
      tags: ['Module dev', 'API integration', 'Performance', 'Refactoring'],
    },
    {
      num: '03', icon: 'fa-globe', featured: true,
      title: 'Website Development',
      desc: 'Corporate sites, e-commerce platforms, custom CMS solutions and high-performance landing pages — designed to convert and built to last.',
      tags: ['Corporate', 'E-commerce', 'CMS', 'Headless', 'SEO-ready'],
    },
    {
      num: '04', icon: 'fa-mobile-alt', featured: false,
      title: 'Web & Mobile Applications',
      desc: 'Cross-platform and native applications engineered for performance, usability and scale. From SaaS dashboards to consumer-facing mobile apps.',
      tags: ['React', 'React Native', 'Flutter', 'iOS', 'Android'],
    },
    {
      num: '05', icon: 'fa-cogs', featured: false,
      title: 'Custom Software Development',
      desc: 'Bespoke internal tools, automation platforms, ERP/CRM integrations and enterprise software that fits your operations exactly.',
      tags: ['ERP', 'CRM', 'Automation', 'Integrations', 'Portals'],
    },
    {
      num: '06', icon: 'fa-hashtag', featured: false,
      title: 'Digital Presence & Social Media',
      desc: 'Complete digital presence management: content strategy, social media management, SEO, paid campaigns and analytics reporting.',
      tags: ['SEO', 'Content', 'Social ads', 'Analytics', 'Branding'],
    },
  ];

  return (
    <section className="section section-alt" id="services">
      <div className="container">
        <div className="text-center">
          <span className="badge">What we deliver</span>
          <h2 className="section-heading">Services built for <em>results</em></h2>
          <p className="section-sub">
            From a single feature to a complete product — our service catalogue covers
            every stage of your digital journey.
          </p>
        </div>
        <div className="services-grid">
          {services.map(s => (
            <div key={s.num} className={`service-card ${s.featured ? 'featured' : ''}`}>
              <span className="service-num">{s.num}</span>
              <i className={`fas ${s.icon} service-icon`}></i>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              <div className="service-tags">
                {s.tags.map(t => <span key={t} className="service-tag">{t}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   HOW IT WORKS
═══════════════════════════════════════════════ */
function HowItWorks() {
  const steps = [
    { n: '01', icon: 'fa-clipboard-list', title: 'Define your needs', desc: 'Tell us about your project, required stack, team size, timeline and budget. A 30-minute call is enough to get started.' },
    { n: '02', icon: 'fa-search', title: 'We match your team', desc: 'Within 24 hours, we propose a curated selection of pre-vetted developers that fit your technical and cultural requirements.' },
    { n: '03', icon: 'fa-handshake', title: 'Interview & confirm', desc: 'Meet the candidates. Run technical interviews. Select your team. We handle contracts, NDAs and onboarding paperwork.' },
    { n: '04', icon: 'fa-rocket', title: 'Start in 72 hours', desc: 'Your team is integrated into your tools, repositories and communication channels — ready to ship from day one.' },
  ];

  return (
    <section className="section" id="how">
      <div className="container">
        <div className="text-center">
          <span className="badge">Process</span>
          <h2 className="section-heading">Up and running in <em>72 hours</em></h2>
          <p className="section-sub">
            No lengthy procurement. No bloated onboarding. A lean, focused process
            designed to get your team working as fast as possible.
          </p>
        </div>
        <div className="how-steps">
          {steps.map(s => (
            <div key={s.n} className="how-step">
              <div className="step-circle">
                <i className={`fas ${s.icon}`}></i>
              </div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   TEAM BUILDER — FULLY INTERACTIVE
═══════════════════════════════════════════════ */

// ── Data: all roles in the pool, tagged by stack/level
const ALL_ROLES = [
  { initials:'FE', name:'Frontend Engineer',     tech:'React / TypeScript',    stacks:['React','Vue'],            levels:['mid','senior'] },
  { initials:'FE', name:'Frontend Engineer',     tech:'Vue / Nuxt.js',         stacks:['Vue'],                    levels:['junior','mid'] },
  { initials:'BE', name:'Backend Developer',     tech:'Node.js / Express',     stacks:['Node.js','JavaScript'],   levels:['mid','senior'] },
  { initials:'BE', name:'Backend Developer',     tech:'Python / Django',        stacks:['Python'],                 levels:['junior','mid','senior'] },
  { initials:'BE', name:'Backend Developer',     tech:'Java / Spring Boot',     stacks:['Java'],                   levels:['mid','senior'] },
  { initials:'FS', name:'Full-Stack Developer',  tech:'React + Node.js',        stacks:['React','Node.js'],        levels:['mid','senior'] },
  { initials:'FS', name:'Full-Stack Developer',  tech:'Vue + Python',           stacks:['Vue','Python'],           levels:['mid'] },
  { initials:'ML', name:'Mobile Developer',      tech:'React Native',           stacks:['React Native','React'],   levels:['mid','senior'] },
  { initials:'FL', name:'Mobile Developer',      tech:'Flutter / Dart',         stacks:['Flutter'],                levels:['junior','mid','senior'] },
  { initials:'DO', name:'DevOps Engineer',       tech:'AWS / Kubernetes',       stacks:['AWS','DevOps'],           levels:['mid','senior'] },
  { initials:'DO', name:'DevOps Engineer',       tech:'Azure / Terraform',      stacks:['DevOps'],                 levels:['mid','senior'] },
  { initials:'QA', name:'QA Specialist',         tech:'Cypress / Playwright',   stacks:['JavaScript','React'],     levels:['junior','mid'] },
  { initials:'DS', name:'Data Engineer',         tech:'Python / Spark / SQL',   stacks:['Python'],                 levels:['mid','senior'] },
  { initials:'PM', name:'Project Manager',       tech:'Scrum / Agile / Jira',   stacks:[],                         levels:['mid','senior'] },
];

// ── Price map: base monthly rate per dev (€)
const RATE_MAP = { junior:1200, mid:2000, senior:3200 };
const HOURS_MULT = { 'Full-time':1, 'Part-time':0.55, 'Project-based':0.7 };
const ENGAGEMENT_DISC = { '1 month':0, '3 months':5, '6 months':10, 'Ongoing':15 };

// ── Derive level from expertise selection
function getLevels(expertise) {
  if (expertise === 'Junior')   return ['junior'];
  if (expertise === 'Mid-level') return ['mid'];
  if (expertise === 'Senior')   return ['senior'];
  return ['junior','mid','senior']; // Mixed
}

// ── Derive level CSS class
function levelClass(lvl) {
  if (lvl === 'senior') return 'level-senior';
  if (lvl === 'mid')    return 'level-mid';
  return 'level-junior';
}

// ── Build preview team from selections
function buildTeam({ teamSize, expertise, stacks }) {
  const sizeMap = { '1 dev':1, '2–3 devs':3, '4–5 devs':5, '6–10 devs':8 };
  const count   = sizeMap[teamSize] ?? 1;
  const levels  = getLevels(expertise);

  // Filter pool: match at least one selected stack OR no stacks selected
  const filtered = ALL_ROLES.filter(r => {
    const stackMatch = stacks.length === 0 || r.stacks.some(s => stacks.includes(s));
    const levelMatch = r.levels.some(l => levels.includes(l));
    return stackMatch && levelMatch;
  });

  // Pick roles up to count, cycling through filtered pool
  const team = [];
  for (let i = 0; i < count; i++) {
    const role = filtered[i % filtered.length];
    // Assign the best matching level from the role's available levels
    const assignedLevel = role.levels.filter(l => levels.includes(l)).slice(-1)[0] ?? levels[0];
    team.push({ ...role, assignedLevel });
  }
  return team;
}

// ── Compute estimated monthly cost
function computeCost({ teamSize, expertise, hours, engagement }) {
  const sizeMap   = { '1 dev':1, '2–3 devs':2.5, '4–5 devs':4, '6–10 devs':7 };
  const count     = sizeMap[teamSize] ?? 1;
  const levelMap  = { 'Junior':'junior', 'Mid-level':'mid', 'Senior':'senior', 'Mixed':'mid' };
  const rate      = RATE_MAP[levelMap[expertise]] ?? RATE_MAP.mid;
  const hMult     = HOURS_MULT[hours] ?? 1;
  const disc      = ENGAGEMENT_DISC[engagement] ?? 0;
  const base      = Math.round(count * rate * hMult);
  const final     = Math.round(base * (1 - disc / 100));
  return { base, final, discount: disc, count: Math.round(count) };
}

function TeamBuilder() {
  const [teamSize,  setTeamSize]  = useState('4–5 devs');
  const [hours,     setHours]     = useState('Full-time');
  const [expertise, setExpertise] = useState('Senior');
  const [engagement,setEngagement]= useState('3 months');
  const [stacks,    setStacks]    = useState(['React','Node.js']);

  const toggleStack = (s) =>
    setStacks(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const team = buildTeam({ teamSize, expertise, stacks });
  const cost = computeCost({ teamSize, expertise, hours, engagement });

  const groups = [
    {
      label:'Team size',
      type:'single',
      options:['1 dev','2–3 devs','4–5 devs','6–10 devs'],
      value: teamSize,
      onChange: setTeamSize,
    },
    {
      label:'Working hours',
      type:'single',
      options:['Full-time','Part-time','Project-based'],
      value: hours,
      onChange: setHours,
    },
    {
      label:'Expertise level',
      type:'single',
      options:['Junior','Mid-level','Senior','Mixed'],
      value: expertise,
      onChange: setExpertise,
    },
    {
      label:'Engagement duration',
      type:'single',
      options:['1 month','3 months','6 months','Ongoing'],
      value: engagement,
      onChange: setEngagement,
    },
    {
      label:'Tech stack (multi-select)',
      type:'multi',
      options:['React','Vue','Node.js','Python','Java','Flutter','AWS','DevOps'],
      value: stacks,
      onChange: toggleStack,
    },
  ];

  return (
    <section className="section builder-section" id="team-builder">
      <div className="container">
        <div className="builder-grid">

          {/* ── LEFT: Configurator ── */}
          <div>
            <span className="badge">Team Configurator</span>
            <h2 className="section-heading">Build your <em>perfect team</em></h2>
            <p className="section-sub" style={{ marginBottom:'36px' }}>
              Select your preferences below and watch your team assemble
              in real time — no package constraints, no forced bundles.
            </p>

            <div className="builder-options">
              {groups.map(g => (
                <div key={g.label} className="builder-group">
                  <div className="builder-group-label">{g.label}</div>
                  <div className="option-pills">
                    {g.options.map(opt => {
                      const isActive = g.type === 'multi'
                        ? g.value.includes(opt)
                        : g.value === opt;
                      return (
                        <span
                          key={opt}
                          className={`option-pill ${isActive ? 'active' : ''}`}
                          onClick={() => g.type === 'multi' ? g.onChange(opt) : g.onChange(opt)}
                          style={{ cursor:'pointer', userSelect:'none' }}
                        >
                          {opt}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Live Preview ── */}
          <div className="builder-preview">
            <div className="preview-header">
              <span className="preview-header-title">
                TEAM_PREVIEW // {teamSize} · {expertise.toLowerCase()} · {hours.toLowerCase()}
              </span>
              <span className="preview-status">{team.length} engineer{team.length !== 1 ? 's' : ''} matched</span>
            </div>

            <div className="preview-body">
              {/* Role list */}
              <div className="preview-roles">
                {team.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'32px', color:'var(--grey)', fontSize:'0.85rem' }}>
                    <i className="fas fa-search" style={{ fontSize:'1.4rem', marginBottom:'10px', display:'block', color:'var(--cyan)' }}></i>
                    No exact match — try adjusting your stack or expertise level.
                  </div>
                ) : (
                  team.map((r, idx) => (
                    <div key={idx} className="role-row" style={{ animation:`fadeUp 0.3s ease ${idx * 0.06}s both` }}>
                      <div className="role-info">
                        <div className="role-avatar">{r.initials}</div>
                        <div>
                          <div className="role-name">{r.name}</div>
                          <div className="role-tech">{r.tech}</div>
                        </div>
                      </div>
                      <span className={`role-level ${levelClass(r.assignedLevel)}`}>
                        {r.assignedLevel}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Estimated cost banner */}
              <div style={{
                background:'var(--cyan-dim)',
                border:'1px solid var(--border)',
                borderRadius:'var(--radius)',
                padding:'14px 18px',
                display:'flex',
                alignItems:'center',
                justifyContent:'space-between',
                marginBottom:'16px',
                flexWrap:'wrap',
                gap:'10px',
              }}>
                <div>
                  <span style={{ fontFamily:'var(--font-mono)', fontSize:'0.68rem', color:'var(--grey)', textTransform:'uppercase', letterSpacing:'0.12em' }}>
                    Estimated monthly cost
                  </span>
                  <div style={{ display:'flex', alignItems:'baseline', gap:'8px', marginTop:'3px' }}>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:'1.6rem', fontWeight:'600', color:'var(--cyan)' }}>
                      €{cost.final.toLocaleString()}
                    </span>
                    {cost.discount > 0 && (
                      <span style={{ fontFamily:'var(--font-mono)', fontSize:'0.78rem', color:'var(--green)' }}>
                        –{cost.discount}% ({engagement} discount)
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <span style={{ fontFamily:'var(--font-mono)', fontSize:'0.68rem', color:'var(--grey)', textTransform:'uppercase', letterSpacing:'0.1em' }}>
                    vs local hire
                  </span>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:'1.1rem', fontWeight:'600', color:'var(--green)', marginTop:'3px' }}>
                    –40% avg saving
                  </div>
                </div>
              </div>

              {/* Summary strip */}
              <div className="preview-summary">
                <div className="summary-item">
                  <span className="summary-num">{team.length}</span>
                  <span className="summary-lbl">Engineers</span>
                </div>
                <div className="summary-item">
                  <span className="summary-num" style={{ fontSize:'0.9rem' }}>{hours}</span>
                  <span className="summary-lbl">Schedule</span>
                </div>
                <div className="summary-item">
                  <span className="summary-num">72h</span>
                  <span className="summary-lbl">Start date</span>
                </div>
                <div className="summary-item">
                  <span className="summary-num" style={{ fontSize:'0.9rem' }}>{engagement}</span>
                  <span className="summary-lbl">Duration</span>
                </div>
              </div>

              {/* CTA */}
              <button
                className="btn-primary"
                style={{ width:'100%', justifyContent:'center', marginTop:'16px' }}
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior:'smooth' })}
              >
                <i className="fas fa-rocket"></i> Request this team configuration
              </button>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   PRICING
═══════════════════════════════════════════════ */
function Pricing() {
  const plans = [
    {
      name: 'Starter',
      price: '1,800',
      period: '/ dev / month',
      desc: 'Ideal for early-stage startups or businesses needing focused development support with a single dedicated developer.',
      popular: false,
      features: [
        { text: '1 dedicated developer', on: true },
        { text: 'Junior to mid-level expertise', on: true },
        { text: 'Full-time or part-time', on: true },
        { text: 'Daily status updates', on: true },
        { text: 'Tech stack of your choice', on: true },
        { text: 'Multi-developer team', on: false },
        { text: 'Dedicated project manager', on: false },
      ],
    },
    {
      name: 'Growth',
      price: '6,500',
      period: '/ team / month',
      desc: 'Our most popular plan for scale-ups and SMBs building serious digital products with a dedicated cross-functional team.',
      popular: true,
      features: [
        { text: '3–5 dedicated developers', on: true },
        { text: 'Mid to senior expertise', on: true },
        { text: 'Full-time engagement', on: true },
        { text: 'Daily standups + Slack access', on: true },
        { text: 'Tech stack of your choice', on: true },
        { text: 'Dedicated project manager', on: true },
        { text: 'Weekly executive reporting', on: false },
      ],
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      desc: 'For enterprises requiring large, specialized teams, complex integrations, or multi-project delivery across time zones.',
      popular: false,
      features: [
        { text: '6–10+ dedicated developers', on: true },
        { text: 'Senior & lead engineers', on: true },
        { text: 'Custom timezone coverage', on: true },
        { text: 'Dedicated project manager', on: true },
        { text: 'Weekly executive reporting', on: true },
        { text: 'SLA-backed delivery guarantee', on: true },
        { text: 'On-site visits available', on: true },
      ],
    },
  ];

  return (
    <section className="section section-alt" id="pricing">
      <div className="container">
        <div className="text-center">
          <span className="badge">Engagement models</span>
          <h2 className="section-heading">Transparent pricing.<br /><em>No hidden fees.</em></h2>
          <p className="section-sub">
            All plans include vetted developers, IP ownership, NDA protection
            and a dedicated account manager. Scale up or down any time.
          </p>
        </div>
        <div className="pricing-grid">
          {plans.map(p => (
            <div key={p.name} className={`pricing-card ${p.popular ? 'popular' : ''}`}>
              {p.popular && <span className="popular-badge">Most popular</span>}
              <div className="pricing-name">{p.name}</div>
              <div className="pricing-price">
                {p.price !== 'Custom' && <span className="currency">€</span>}
                {p.price}
                <span className="period">{p.period}</span>
              </div>
              <p className="pricing-desc">{p.desc}</p>
              <div className="pricing-features">
                {p.features.map(f => (
                  <div key={f.text} className={`pricing-feature ${f.on ? '' : 'off'}`}>
                    <i className={`fas ${f.on ? 'fa-check' : 'fa-times'}`}></i>
                    {f.text}
                  </div>
                ))}
              </div>
              {p.popular
                ? <button className="btn-primary" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                    <i className="fas fa-rocket"></i> Get started
                  </button>
                : <button className="btn-outline" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                    {p.name === 'Enterprise' ? 'Talk to sales' : 'Get started'}
                  </button>
              }
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   WHY US
═══════════════════════════════════════════════ */
function WhyUs() {
  const reasons = [
    { n: '01', title: 'Pre-vetted talent only', desc: 'Every developer in our network passes a rigorous technical screening — code tests, system design challenges and communication assessments. You interview the shortlist, not the full pool.' },
    { n: '02', title: 'Cost-efficient, not cheap', desc: 'We offer 30–50% savings vs equivalent local hires — but never compromise on seniority or quality. You get the same calibre of engineer at a fraction of the market rate.' },
    { n: '03', title: 'You stay in control', desc: 'Your team, your tools, your processes. Developers integrate into your existing stack (Jira, GitHub, Notion, Slack) and report directly to your project managers.' },
    { n: '04', title: 'Zero lock-in contracts', desc: 'Monthly rolling engagements. Scale up, scale down, or exit with 30 days notice. No penalties, no long-term commitments forced upon you.' },
    { n: '05', title: 'Cross-timezone coverage', desc: 'Whether you need overlap with CET, EST or AEST — we build teams that fit your working hours and ensure real-time collaboration when it matters.' },
    { n: '06', title: 'Full legal protection', desc: 'Comprehensive NDAs, data processing agreements and IP assignment clauses are standard on every engagement. Your business stays protected.' },
  ];

  return (
    <section className="section" id="why">
      <div className="container">
        <div className="text-center">
          <span className="badge">Why Strategix</span>
          <h2 className="section-heading">The smarter way to <em>scale your team</em></h2>
          <p className="section-sub">
            We've helped 80+ companies across Europe, the US and beyond
            build high-performing development teams. Here's why they choose us.
          </p>
        </div>
        <div className="why-grid">
          {reasons.map(r => (
            <div key={r.n} className="why-card">
              <span className="why-card-num">{r.n}</span>
              <h3>{r.title}</h3>
              <p>{r.desc}</p>
            </div>
          ))}
        </div>
        <div className="why-bottom">
          {[
            { num: '200+', lbl: 'Projects shipped' },
            { num: '40%', lbl: 'Average cost saving' },
            { num: '72h', lbl: 'Average team start' },
            { num: '4.9/5', lbl: 'Client rating' },
          ].map(s => (
            <div key={s.lbl} className="why-stat">
              <span className="why-stat-num">{s.num}</span>
              <span className="why-stat-lbl">{s.lbl}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   CONTACT
═══════════════════════════════════════════════ */
function Contact() {
  const [form, setForm] = useState({ name:'', company:'', email:'', service:'', team_size:'', message:'' });
  const [sent, setSent] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = e => {
    e.preventDefault();
    // Replace with your EmailJS/API call
    setSent(true);
    setTimeout(() => setSent(false), 6000);
  };

  const infos = [
    { icon: 'fa-envelope', label: 'Email', value: 'contact@strateg-ix.com' },
    { icon: 'fa-phone', label: 'Phone', value: '+32 2 800 00 00' },
    { icon: 'fa-map-marker-alt', label: 'Headquarters', value: 'Brussels, Belgium' },
    { icon: 'fa-clock', label: 'Response time', value: 'Within 4 business hours' },
  ];

  return (
    <section className="section contact-section" id="contact">
      <div className="container">
        <div className="text-center">
          <span className="badge">Start today</span>
          <h2 className="section-heading">Let's build your <em>dedicated team</em></h2>
          <p className="section-sub">
            Tell us about your project and we'll get back to you
            within 4 hours with a tailored team proposal.
          </p>
        </div>
        <div className="contact-grid">
          <div className="contact-info-stack">
            {infos.map(i => (
              <div key={i.label} className="contact-info-card">
                <div className="contact-info-icon"><i className={`fas ${i.icon}`}></i></div>
                <div>
                  <div className="contact-info-label">{i.label}</div>
                  <div className="contact-info-value">{i.value}</div>
                </div>
              </div>
            ))}
            <div className="contact-info-card" style={{ flexDirection:'column', gap:'12px' }}>
              <div className="contact-info-label" style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <i className="fas fa-check-circle" style={{ color:'var(--green)' }}></i>
                Our commitment to you
              </div>
              {['No obligation consultation', 'Team proposal within 24h', 'Free 1-week trial on Growth plan', 'Cancel any time — no penalty'].map(c => (
                <div key={c} style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'0.85rem', color:'var(--grey-light)' }}>
                  <i className="fas fa-arrow-right" style={{ color:'var(--cyan)', fontSize:'0.65rem' }}></i>{c}
                </div>
              ))}
            </div>
          </div>

          <div className="contact-form-wrap">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full name *</label>
                  <input className="form-input" name="name" value={form.name} onChange={handleChange}
                    placeholder="John Smith" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Company *</label>
                  <input className="form-input" name="company" value={form.company} onChange={handleChange}
                    placeholder="Acme Corp" required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Business email *</label>
                <input className="form-input" name="email" type="email" value={form.email} onChange={handleChange}
                  placeholder="john@company.com" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Service needed</label>
                  <select className="form-select" name="service" value={form.service} onChange={handleChange}>
                    <option value="">Select a service</option>
                    <option>Full project development</option>
                    <option>Partial outsourcing</option>
                    <option>Website development</option>
                    <option>Web / Mobile app</option>
                    <option>Custom software</option>
                    <option>Digital presence</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Team size</label>
                  <select className="form-select" name="team_size" value={form.team_size} onChange={handleChange}>
                    <option value="">Select team size</option>
                    <option>1 developer</option>
                    <option>2–3 developers</option>
                    <option>4–5 developers</option>
                    <option>6–10 developers</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Project brief *</label>
                <textarea className="form-textarea" name="message" value={form.message} onChange={handleChange}
                  placeholder="Describe your project, timeline, tech stack and any specific requirements..." required />
              </div>
              <div className="form-submit">
                {sent
                  ? <div style={{ display:'flex', alignItems:'center', gap:'8px', color:'var(--green)', fontSize:'0.875rem' }}>
                      <i className="fas fa-check-circle"></i> Message sent — we'll be in touch within 4 hours.
                    </div>
                  : <button type="submit" className="btn-primary">
                      <i className="fas fa-paper-plane"></i> Send project brief
                    </button>
                }
                <span className="form-note">
                  <i className="fas fa-lock"></i> Your data is encrypted and never shared.
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════════ */
function Footer() {
  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div>
            <div className="footer-brand-name">STRATEG<span>IX</span></div>
            <p className="footer-brand-desc">
              Dedicated development teams for ambitious businesses.
              Quality-first, cost-efficient, and ready in 72 hours.
            </p>
            <div className="footer-social">
              {[
                { icon: 'fa-linkedin-in', href: '#' },
                { icon: 'fa-twitter', href: '#' },
                { icon: 'fa-github', href: '#' },
              ].map(s => (
                <a key={s.icon} href={s.href} className="social-btn">
                  <i className={`fab ${s.icon}`}></i>
                </a>
              ))}
            </div>
          </div>
          <div>
            <div className="footer-col-title">Services</div>
            <div className="footer-links">
              {['Full Project Dev', 'Partial Outsourcing', 'Website Development', 'Web & Mobile Apps', 'Custom Software', 'Social Media'].map(l => (
                <span key={l} className="footer-link" style={{ cursor:'pointer' }}
                  onClick={() => scrollTo('services')}>{l}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="footer-col-title">Company</div>
            <div className="footer-links">
              {[['About', 'about'], ['How It Works', 'how'], ['Pricing', 'pricing'], ['Why Us', 'why'], ['Contact', 'contact']].map(([l, id]) => (
                <span key={l} className="footer-link" style={{ cursor:'pointer' }}
                  onClick={() => scrollTo(id)}>{l}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="footer-col-title">Get started</div>
            <p style={{ fontSize:'0.85rem', color:'var(--grey)', marginBottom:'16px', lineHeight:'1.5' }}>
              Ready to build your team? Book a free 30-minute discovery call.
            </p>
            <button className="btn-primary" onClick={() => scrollTo('contact')}>
              <i className="fas fa-calendar"></i> Book a call
            </button>
          </div>
        </div>
        <div className="footer-bottom">
          <span className="footer-copy">© 2026 Strategix SRL. All rights reserved.</span>
          <div className="footer-legal">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
            <a href="#">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════
   APP ROOT
═══════════════════════════════════════════════ */
export default function App() {
  return (
    <>
      <Navbar />
      <Hero />
      <ClientsStrip />
      <About />
      <Services />
      <HowItWorks />
      <TeamBuilder />
      <Pricing />
      <WhyUs />
      <Contact />
      <Footer />
    </>
  );
}